import { avaliador, type Arvore, type NoMeta, type Valores } from "./metasArvore";
import { fracaoContribuicao } from "./metasFormato";

/** Nível do N4 abaixo da loja na árvore da revisão (loja 0, N1 1, N2 2, N3 3, N4 4). */
export const NIVEL_N4 = 4;

/** Uma linha do relatório N4, no grão do `set_nivel4_*.csv`: N2 (categoria) × N3 × N4, somado entre lojas — ou o TOTAL da categoria. */
export interface LinhaRelatorioN4 {
  categoria: string;
  periodo: string;
  linhaTipo: "item" | "TOTAL";
  secao: string;
  subsecao: string;
  /** Fração da venda do mês anterior da categoria; `null` no TOTAL. */
  participacaoMesAnterior: number | null;
  vendaMesAnterior: number;
  metaVenda: number;
  metaContribuicao: number;
  /** Meta de contribuição ÷ meta de venda; 0 sem meta. */
  metaMargem: number;
  /** Mesma regra do arquivo do cliente: meta do mês ÷ 30, fixo. */
  metaVendaDia: number;
}

interface Acumulado {
  categoria: string;
  secao: string;
  subsecao: string;
  vendaMesAnterior: number;
  metaVenda: number;
  metaContribuicao: number;
}

/**
 * N2 × N3 × N4 consolidados entre as `lojas` a partir da árvore carregada até
 * o N4 (ver `carregarAteN4`). Meta de venda é a ajustada — exata, porque o
 * N4 está carregado e o avaliador já considera o ajuste no próprio nó, acima
 * e abaixo dele. A contribuição segue a mesma conta do relatório comercial
 * (% do motor sobre a meta ajustada). Venda do mês anterior é a última
 * posição do histórico (a janela termina no mês anterior ao da meta).
 * Só categorias no nível N4: produto solto num nível mais raso fica de fora.
 * Cada categoria (N2) fecha com uma linha TOTAL.
 */
export function linhasRelatorioN4(
  arvore: Arvore,
  ajustes: Valores,
  lojas: string[],
  periodo: string,
): LinhaRelatorioN4[] {
  const { nos, filhos } = arvore;
  const { ajustada } = avaliador(arvore, ajustes);
  const agregado = new Map<string, Acumulado>();

  const visitar = (no: NoMeta, caminho: NoMeta[]) => {
    if (no.tipoNodo === "PRODUTO") return;
    if (no.nivel === NIVEL_N4) {
      // caminho = loja, N1, N2, N3 (o próprio N4 não está nele)
      const n2Nome = caminho[2]?.nome;
      const n3Nome = caminho[3]?.nome;
      if (!n2Nome || !n3Nome) return;
      const meta = ajustada(no.id);
      const frac = fracaoContribuicao([
        { percEnviado: no.percContribuicaoEnviado, contribuicao: no.contribuicaoCalculada, calculada: no.calculado },
      ]);
      const contribuicao = frac !== null && no.calculado ? meta * frac : no.contribuicaoCalculada;
      const chave = [n2Nome, n3Nome, no.nome].join("\u0000");
      const atual = agregado.get(chave) ?? {
        categoria: n2Nome,
        secao: n3Nome,
        subsecao: no.nome,
        vendaMesAnterior: 0,
        metaVenda: 0,
        metaContribuicao: 0,
      };
      atual.vendaMesAnterior += no.historico[no.historico.length - 1] ?? 0;
      atual.metaVenda += meta;
      atual.metaContribuicao += contribuicao;
      agregado.set(chave, atual);
      return;
    }
    (filhos[no.id] ?? []).forEach((id) => {
      const filho = nos[id];
      if (filho) visitar(filho, [...caminho, no]);
    });
  };
  lojas.forEach((id) => {
    const loja = nos[id];
    if (loja) visitar(loja, []);
  });

  const porCategoria = new Map<string, Acumulado[]>();
  agregado.forEach((item) => porCategoria.set(item.categoria, [...(porCategoria.get(item.categoria) ?? []), item]));

  const arredondar = (valor: number) => Math.round(valor * 100) / 100;
  const margem = (contribuicao: number, venda: number) => (venda ? contribuicao / venda : 0);
  const linhas: LinhaRelatorioN4[] = [];
  [...porCategoria.keys()].sort((a, b) => a.localeCompare(b)).forEach((categoria) => {
    const itens = porCategoria.get(categoria)!;
    const totalAnterior = itens.reduce((acc, item) => acc + item.vendaMesAnterior, 0);
    itens
      .sort((a, b) => b.vendaMesAnterior - a.vendaMesAnterior || a.secao.localeCompare(b.secao) || a.subsecao.localeCompare(b.subsecao))
      .forEach((item) =>
        linhas.push({
          categoria,
          periodo,
          linhaTipo: "item",
          secao: item.secao,
          subsecao: item.subsecao,
          participacaoMesAnterior: totalAnterior ? item.vendaMesAnterior / totalAnterior : 0,
          vendaMesAnterior: arredondar(item.vendaMesAnterior),
          metaVenda: arredondar(item.metaVenda),
          metaContribuicao: arredondar(item.metaContribuicao),
          metaMargem: margem(item.metaContribuicao, item.metaVenda),
          metaVendaDia: arredondar(item.metaVenda / 30),
        }),
      );
    const metaVenda = itens.reduce((acc, item) => acc + item.metaVenda, 0);
    const metaContribuicao = itens.reduce((acc, item) => acc + item.metaContribuicao, 0);
    linhas.push({
      categoria,
      periodo,
      linhaTipo: "TOTAL",
      secao: "",
      subsecao: "",
      participacaoMesAnterior: null,
      vendaMesAnterior: arredondar(totalAnterior),
      metaVenda: arredondar(metaVenda),
      metaContribuicao: arredondar(metaContribuicao),
      metaMargem: margem(metaContribuicao, metaVenda),
      metaVendaDia: arredondar(metaVenda / 30),
    });
  });
  return linhas;
}
